import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, X, UserCog } from "lucide-react";
import { api, errMsg, inr, today } from "@/lib/api";
import { PageHeader, Field } from "@/components/common";
import { PartyNameInput } from "@/components/party";
import { useAuth } from "@/context/AuthContext";

const party = { name: "", gstin: "", pan: "", contact_person: "", address: "", phone: "" };
const EMPTY = {
  booking_date: today(), consignor: { ...party }, consignee: { ...party }, consignor_id: null, consignee_id: null, billing_party: "consignor",
  pickup_location: "", drop_location: "", pickup_points: [{ name: "", address: "", city: "" }], drop_points: [{ name: "", address: "", city: "" }],
  material_type: "", packages: 0, weight_tons: 0, freight_type: "fixed", rate_per_mt: "", rcm: false,
  freight_amount: "", advance_paid: 0, gst_rate: 5, show_freight_on_lr: true, gst_type: "cgst_sgst", gst_paid_by: "consignor",
  invoice_value: 0, eway_bill: "", eta: "", loading_date: "", unloading_date: "", remarks: "", vehicle_id: "", driver_id: "",
  lr_number: "", invoice_number: "", party_booking_id: "", brokerage: 0, extra_charges: [],
};
const KEYS = Object.keys(EMPTY);
const EXTRA_TYPES = [["detention", "Detention"], ["halting", "Halting"], ["extra_loading", "Extra Loading"], ["unloading", "Unloading / Labour"], ["other", "Other"]];

function ExtraCharges({ list, onChange }) {
  const upd = (i, k, v) => onChange(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="rounded-lg border p-3" data-testid="extra-charges">
      <div className="mb-2 flex items-center justify-between text-sm font-semibold">Extra Charges (Detention / Halting / Loading)
        <Button type="button" size="sm" variant="outline" data-testid="add-extra-charge-btn" onClick={() => onChange([...list, { type: "detention", amount: "", note: "" }])}><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
      </div>
      {list.map((x, i) => (
        <div key={i} className="mb-2 grid grid-cols-[1fr_90px_auto] gap-2" data-testid={`extra-charge-row-${i}`}>
          <select className="native-select" data-testid={`extra-type-${i}`} value={x.type} onChange={(e) => upd(i, "type", e.target.value)}>{EXTRA_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <Input type="number" min="1" step="0.01" required placeholder="₹" data-testid={`extra-amount-${i}`} value={x.amount} onChange={(e) => upd(i, "amount", e.target.value)} />
          <Button type="button" size="icon" variant="ghost" data-testid={`extra-remove-${i}`} onClick={() => onChange(list.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
          <Input className="col-span-3" placeholder="Note (e.g. 2 days @ ₹1500)" data-testid={`extra-note-${i}`} value={x.note || ""} onChange={(e) => upd(i, "note", e.target.value)} />
        </div>
      ))}
      {!list.length && <p className="text-xs text-muted-foreground">Detention ya extra charge lage to yahan add karein — invoice aur GST auto update honge.</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-base p-5 md:p-6 fade-up">
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function StopsEditor({ kind, label, stops, onChange }) {
  const upd = (i, k, v) => onChange(stops.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-3 sm:col-span-2" data-testid={`${kind}-points`}>
      {stops.map((s, i) => (
        <div key={i} className="grid grid-cols-1 gap-2 rounded-lg border bg-secondary/30 p-3 sm:grid-cols-[1fr_1.4fr_1fr_auto] sm:items-end">
          <Field label={`${label} ${i + 1} · Name`}><Input data-testid={`${kind}-${i}-name-input`} value={s.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Plant / Warehouse" /></Field>
          <Field label="Address"><Input data-testid={`${kind}-${i}-address-input`} value={s.address} onChange={(e) => upd(i, "address", e.target.value)} /></Field>
          <Field label="City *"><Input data-testid={`${kind}-${i}-city-input`} required value={s.city} onChange={(e) => upd(i, "city", e.target.value)} /></Field>
          <button type="button" data-testid={`${kind}-${i}-remove-btn`} disabled={stops.length === 1} onClick={() => onChange(stops.filter((_, j) => j !== i))}
            className="flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground hover:text-rose-600 disabled:opacity-30"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid={`${kind}-add-btn`} onClick={() => onChange([...stops, { name: "", address: "", city: "" }])}>
        <Plus className="mr-1 h-4 w-4" />Add {label}
      </Button>
    </div>
  );
}

function PartyFields({ who, value, onChange, onPick, required = true }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <>
      <Field label={required ? "Party / Company Name *" : "Party / Company Name (optional)"} className="sm:col-span-2"><PartyNameInput who={who} required={required} value={value.name} onChange={(name) => onChange({ ...value, name })} onPick={onPick} /></Field>
      <Field label="Contact Person"><Input data-testid={`${who}-contact-input`} value={value.contact_person || ""} onChange={set("contact_person")} /></Field>
      <Field label="GST Number"><Input data-testid={`${who}-gstin-input`} value={value.gstin} onChange={set("gstin")} placeholder="33AAACU1234F1Z5" /></Field>
      <Field label="PAN"><Input data-testid={`${who}-pan-input`} value={value.pan || ""} onChange={set("pan")} /></Field>
      <Field label="Phone"><Input data-testid={`${who}-phone-input`} value={value.phone} onChange={set("phone")} /></Field>
      <Field label="Full Address"><Input data-testid={`${who}-address-input`} value={value.address} onChange={set("address")} /></Field>
    </>
  );
}

function MarketHireFields({ m, setM, showFreight, lrWeight = 0 }) {
  const [vendors, setVendors] = useState([]);
  const [known, setKnown] = useState({});
  useEffect(() => {
    api.get("/market/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/market/trips").then((r) => {
      const map = {};
      [...r.data].reverse().forEach((t) => { map[t.vehicle_number] = { vendor_id: t.vendor_id, driver_name: t.driver_name, driver_phone: t.driver_phone }; });
      setKnown(map);
    }).catch(() => {});
  }, []);
  const onVehicle = (e) => {
    const num = e.target.value.toUpperCase().replace(/\s+/g, "");
    const k = known[num];
    setM({ ...m, vehicle_number: num, ...(k ? { vendor_id: k.vendor_id, driver_name: m.driver_name || k.driver_name || "", driver_phone: m.driver_phone || k.driver_phone || "" } : {}) });
  };
  return (
    <div className="space-y-3" data-testid="market-hire-fields">
      <Field label="Market Gaadi Number *">
        <Input required list="market-vehicle-list" data-testid="mh-vehicle-input" className="uppercase" value={m.vehicle_number} onChange={onVehicle} placeholder="MH12AB1234" />
        <datalist id="market-vehicle-list">{Object.keys(known).map((v) => <option key={v} value={v} />)}</datalist>
      </Field>
      <Field label="Vendor Name *">
        <select required data-testid="mh-vendor-select" className="native-select" value={m.vendor_id} onChange={(e) => setM({ ...m, vendor_id: e.target.value })}>
          <option value="">Select registered vendor…</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.phone ? `${v.name} · ${v.phone}` : v.name}</option>)}
        </select>
        {!vendors.length && <p className="mt-1 text-xs text-amber-600">No vendors yet — register one in Market Vehicle & Vendors → Vendors.</p>}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Driver Name"><Input data-testid="mh-driver-name-input" value={m.driver_name} onChange={(e) => setM({ ...m, driver_name: e.target.value })} /></Field>
        <Field label="Driver Mobile"><Input data-testid="mh-driver-phone-input" value={m.driver_phone} onChange={(e) => setM({ ...m, driver_phone: e.target.value })} /></Field>
      </div>
      {showFreight && <MarketFreight m={m} setM={setM} lrWeight={lrWeight} />}
      <p className="text-[11px] text-muted-foreground">A Market Trip is created automatically — add documents, TDS, advance there.</p>
    </div>
  );
}

export const hireFreight = (m, lrWeight) => (m.rate_type === "per_mt" ? Math.round((Number(m.rate) || 0) * (Number(m.weight || lrWeight) || 0) * 100) / 100 : Number(m.freight) || 0);

function MarketFreight({ m, setM, lrWeight }) {
  const perMt = m.rate_type === "per_mt";
  return (
    <div className="space-y-3">
      <Field label="Hire Rate Type">
        <select data-testid="mh-rate-type-select" className="native-select" value={m.rate_type || "fixed"} onChange={(e) => setM({ ...m, rate_type: e.target.value })}>
          <option value="fixed">Fixed Amount</option>
          <option value="per_mt">Per Metric Ton (Per MT)</option>
        </select>
      </Field>
      {perMt ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rate per MT (₹) *"><Input type="number" min="0" step="0.01" data-testid="mh-rate-input" value={m.rate || ""} onChange={(e) => setM({ ...m, rate: e.target.value })} /></Field>
            <Field label="Weight (MT) *"><Input type="number" min="0" step="0.001" data-testid="mh-weight-input" value={m.weight ?? ""} placeholder={lrWeight ? String(lrWeight) : ""} onChange={(e) => setM({ ...m, weight: e.target.value })} /></Field>
          </div>
          <div className="rounded-md bg-accent px-3 py-2 text-sm" data-testid="mh-freight-total">Hire Freight to Vendor <span className="font-mono font-semibold">₹{hireFreight(m, lrWeight).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
          {!m.weight && lrWeight > 0 && <p className="text-[11px] text-muted-foreground">Weight khali hai to LR ka weight ({lrWeight} MT) liya jaayega.</p>}
        </>
      ) : (
        <Field label="Hire Freight to Vendor (₹)"><Input type="number" min="0" data-testid="mh-freight-input" value={m.freight} onChange={(e) => setM({ ...m, freight: e.target.value })} /></Field>
      )}
    </div>
  );
}

function InvoiceNoField({ f, setF, isNew }) {
  const [auto, setAuto] = useState(isNew);
  const [next, setNext] = useState("");
  useEffect(() => {
    if (!auto) return;
    api.get("/bookings/next-invoice-no").then((r) => { setNext(r.data.invoice_number); if (isNew) setF((p) => ({ ...p, invoice_number: "" })); }).catch(() => {});
  }, [auto, isNew, setF]);
  const toggle = (v) => {
    setAuto(v);
    if (v) setF((p) => ({ ...p, invoice_number: isNew ? "" : next || p.invoice_number }));
  };
  useEffect(() => {
    if (auto && !isNew && next) setF((p) => ({ ...p, invoice_number: next }));
  }, [auto, isNew, next, setF]);
  return (
    <Field label="Invoice Number">
      <div className="flex items-center gap-2">
        <Input data-testid="invoice-number-input" className="uppercase" disabled={auto} value={auto && isNew ? next : f.invoice_number || ""}
          onChange={(e) => setF({ ...f, invoice_number: e.target.value })} placeholder="e.g. SRT/3037" />
        <label className="flex shrink-0 items-center gap-1.5 text-xs font-semibold" title="Pichle bill ke aage wala number">
          <Switch data-testid="invoice-auto-switch" checked={auto} onCheckedChange={toggle} />Auto next
        </label>
      </div>
      {auto && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="invoice-auto-hint">Pichle bill ke aage wala number: <b>{next || "…"}</b>{isNew ? " (save karte waqt pakka hoga)" : ""}</p>}
    </Field>
  );
}

export default function BookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hireMode, setHireMode] = useState("own");
  const [mh, setMh] = useState({ vehicle_number: "", vendor_id: "", driver_name: "", driver_phone: "", freight: "", rate_type: "fixed", rate: "", weight: "" });
  const [existingMt, setExistingMt] = useState(null);
  const [params] = useSearchParams();
  const [pbs, setPbs] = useState([]);

  const applyBooking = async (pbId, base) => {
    if (!pbId) return setF((prev) => ({ ...prev, party_booking_id: "" }));
    try {
      const { data: pb } = await api.get(`/party-bookings/${pbId}`);
      const party = await api.get("/parties").then((r) => r.data.find((x) => x.id === pb.party_id) || { name: pb.party_name }).catch(() => ({ name: pb.party_name }));
      setF((prev) => ({
        ...(base || prev), party_booking_id: pb.id, booking_date: pb.booking_date,
        consignor: { name: party.name || pb.party_name, gstin: party.gstin || "", pan: party.pan || "", contact_person: party.contact_person || "", phone: party.phone || "", address: [party.address, party.city, party.state].filter(Boolean).join(", ") },
        consignor_id: pb.party_id,
        pickup_points: [{ name: "", address: "", city: pb.from_location }], drop_points: [{ name: "", address: "", city: pb.to_location }],
        ...(pb.freight !== undefined ? { freight_type: pb.rate_type, rate_per_mt: pb.rate_type === "per_mt" ? pb.rate : "", weight_tons: pb.rate_type === "per_mt" ? pb.weight_mt : (base || prev).weight_tons, freight_amount: pb.freight, brokerage: pb.brokerage || 0 } : {}),
        lr_number: (base || prev).lr_number || (pb.linked_lrs.length ? "" : pb.lr_number || ""),
        ...(pb.material_type ? { material_type: pb.material_type } : {}),
        ...(pb.vehicle_source === "own" && pb.vehicle_id ? { vehicle_id: pb.vehicle_id } : {}),
      }));
      if (pb.vehicle_source) setHireMode(pb.vehicle_source === "vendor" ? "market" : "own");
      if (pb.vehicle_source === "vendor") setMh((m) => ({ ...m, vendor_id: pb.vendor_id || "", vehicle_number: pb.vehicle_number || "", rate_type: pb.hire_rate_type || "fixed", rate: pb.hire_rate_type === "per_mt" ? pb.hire_rate : "", freight: pb.hire_rate_type === "per_mt" ? "" : pb.hire_freight || "", weight: pb.hire_rate_type === "per_mt" ? pb.weight_mt : "" }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    api.get("/party-bookings").then((r) => setPbs(r.data)).catch(() => {});
    const fb = params.get("from_booking");
    if (!id && fb) applyBooking(fb, EMPTY);
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data));
    if (id) api.get(`/bookings/${id}`).then((r) => {
      const b = r.data;
      const pts = (k, legacy) => (b[k]?.length ? b[k] : [{ name: "", address: "", city: b[legacy] || "" }]);
      setF({ ...Object.fromEntries(KEYS.map((k) => [k, b[k] ?? EMPTY[k]])), pickup_points: pts("pickup_points", "pickup_location"), drop_points: pts("drop_points", "drop_location") });
      if (b.market_trip_id) setExistingMt({ id: b.market_trip_id, vehicle_number: b.market_vehicle_number });
    });
  }, [id]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const applyParty = (side, p) => setF((prev) => ({
    ...prev,
    [side]: { name: p.name, gstin: p.gstin || "", pan: p.pan || "", contact_person: p.contact_person || "", phone: p.phone || "", address: [p.address, p.city, p.state].filter(Boolean).join(", ") },
    [`${side}_id`]: p.id,
  }));
  const editParty = (side) => (v) => setF((prev) => ({ ...prev, [side]: v, [`${side}_id`]: v.name !== prev[side].name ? null : prev[`${side}_id`] }));
  const [driverOverride, setDriverOverride] = useState(false);
  const currentDriver = f.vehicle_id ? drivers.find((d) => d.current_vehicle_id === f.vehicle_id && d.employment_status === "Active") : null;
  const useAutoDriver = !!currentDriver && !driverOverride;
  const [lastDel, setLastDel] = useState(null);
  const onVehicle = (e) => {
    const vid = e.target.value;
    const match = drivers.find((d) => d.current_vehicle_id === vid && d.employment_status === "Active");
    setDriverOverride(false);
    setF({ ...f, vehicle_id: vid, driver_id: vid ? (match ? match.id : "") : f.driver_id });
    setLastDel(null);
    if (!id && vid) {
      api.get("/bookings/vehicle-last-delivered", { params: { vehicle_id: vid } }).then((r) => {
        if (!r.data.date) return;
        setLastDel(r.data);
        setF((p) => (p.vehicle_id === vid ? { ...p, booking_date: r.data.date } : p));
      }).catch(() => {});
    }
  };
  useEffect(() => {
    if (id && drivers.length && f.driver_id && currentDriver && f.driver_id !== currentDriver.id) setDriverOverride(true);
  }, [drivers]); // eslint-disable-line
  const { role } = useAuth();
  const hideFreight = role === "operation";
  const perMt = f.freight_type === "per_mt";
  const freight = perMt ? Math.round((Number(f.weight_tons) || 0) * (Number(f.rate_per_mt) || 0) * 100) / 100 : Number(f.freight_amount) || 0;
  const extras = (f.extra_charges || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const taxable = freight + extras;
  const gst = f.rcm ? 0 : (taxable * Number(f.gst_rate || 0)) / 100;
  const total = taxable + gst;

  const submit = async (e) => {
    e.preventDefault();
    const market = hireMode === "market" && !existingMt;
    if (market && ["admin", "manager"].includes(role) && mh.rate_type === "per_mt" && !(Number(mh.rate) > 0 && Number(mh.weight || f.weight_tons) > 0)) {
      return toast.error("Per MT hire: Rate per MT aur Weight (MT) dono bharein");
    }
    setBusy(true);
    const body = { ...f, freight_amount: hideFreight ? 0 : freight, rate_per_mt: hideFreight ? 0 : Number(f.rate_per_mt) || 0, advance_paid: hideFreight ? 0 : Number(f.advance_paid), gst_rate: hideFreight ? 0 : Number(f.gst_rate), rcm: hideFreight ? false : f.rcm, packages: Number(f.packages), weight_tons: Number(f.weight_tons), invoice_value: hideFreight ? 0 : Number(f.invoice_value), brokerage: hideFreight ? 0 : Number(f.brokerage) || 0, extra_charges: hideFreight ? [] : (f.extra_charges || []).filter((x) => Number(x.amount) > 0).map((x) => ({ type: x.type, amount: Number(x.amount), note: x.note || "" })), party_booking_id: f.party_booking_id || null, vehicle_id: market || existingMt ? null : f.vehicle_id || null, driver_id: market || existingMt ? null : f.driver_id || null };
    try {
      let data;
      try {
        ({ data } = id ? await api.put(`/bookings/${id}`, body) : await api.post("/bookings", body));
      } catch (err) {
        const dup = !id && err.response?.status === 409 && String(err.response?.data?.detail || "").startsWith("DUPLICATE");
        if (!dup || !window.confirm(`⚠ ${errMsg(err).replace("DUPLICATE: ", "")}.\n\nKya ye sach me naya alag trip hai? OK = phir bhi naya LR banao`)) throw err;
        ({ data } = await api.post("/bookings?allow_duplicate=true", body));
      }
      if (market) {
        const v = await api.get("/market/vendors").then((r) => r.data.find((x) => x.id === mh.vendor_id));
        try {
          await api.post("/market/trips", { booking_id: data.id, vendor_id: mh.vendor_id, vehicle_number: mh.vehicle_number, driver_name: mh.driver_name, driver_phone: mh.driver_phone, freight: hireFreight(mh, Number(f.weight_tons) || 0), hire_rate_type: mh.rate_type || "fixed", hire_rate: mh.rate_type === "per_mt" ? Number(mh.rate) || 0 : 0, hire_weight: mh.rate_type === "per_mt" ? Number(mh.weight || f.weight_tons) || 0 : 0, tds_percent: v ? (v.declaration_valid ? 0 : v.default_tds_percent) : 0 });
        } catch (err) { toast.error(`LR saved, but market trip failed: ${errMsg(err)}`); }
      }
      toast.success(id ? "LR updated" : `LR ${data.lr_number} created`);
      navigate(`/bookings/${data.id}`);
    } catch (err) { toast.error(errMsg(err).replace("DUPLICATE: ", "")); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form">
      <PageHeader title={id ? "Edit Manual LR" : "New Manual LR / Bilty"} subtitle="Paper LR details, freight with GST and dispatch assignment">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="booking-cancel-btn">Cancel</Button>
        <Button data-testid="booking-submit-btn" disabled={busy}>{busy ? "Saving…" : id ? "Save Changes" : "Create LR"}</Button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Section title="LR & Party Booking Link">
            <Field label="LR Number"><Input data-testid="lr-number-input" className="uppercase" value={f.lr_number} onChange={set("lr_number")} placeholder="Blank = auto (LR-00001)" /></Field>
            <InvoiceNoField f={f} setF={setF} isNew={!id} />
            <Field label="Link to Party Booking (optional)">
              <select data-testid="link-party-booking-select" className="native-select" value={f.party_booking_id || ""} onChange={(e) => applyBooking(e.target.value)}>
                <option value="">— Standalone LR (no booking) —</option>
                {pbs.map((b) => <option key={b.id} value={b.id}>{`${b.booking_no} · ${b.party_name} · ${b.from_location} → ${b.to_location} · ${b.status}`}</option>)}
              </select>
            </Field>
            {f.party_booking_id && <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="link-hint">Party, route and freight filled from the booking — you can still edit them.</p>}
          </Section>
          <Section title="Consignor (Sender)"><PartyFields who="consignor" value={f.consignor} onChange={editParty("consignor")} onPick={(p) => applyParty("consignor", p)} /></Section>
          <Section title="Consignee (Receiver)"><PartyFields who="consignee" required={f.billing_party === "consignee"} value={f.consignee} onChange={editParty("consignee")} onPick={(p) => applyParty("consignee", p)} /></Section>
          <Section title="Route & Material">
            <Field label="Booking Date"><Input data-testid="booking-date-input" type="date" required value={f.booking_date} onChange={set("booking_date")} />
              {lastDel?.date && f.booking_date === lastDel.date && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="booking-date-hint">Is gaadi ki pichhli trip {lastDel.lr_number} ki Loading Point tarikh se bhari gayi — badal sakte hain</p>}</Field>
            <Field label="Material Type"><Input data-testid="material-input" required value={f.material_type} onChange={set("material_type")} placeholder="Cement Bags" /></Field>
            <div className="label-xs sm:col-span-2">Loading Points</div>
            <StopsEditor kind="pickup" label="Loading Point" stops={f.pickup_points} onChange={(v) => setF({ ...f, pickup_points: v })} />
            <div className="label-xs sm:col-span-2">Unloading Points</div>
            <StopsEditor kind="drop" label="Unloading Point" stops={f.drop_points} onChange={(v) => setF({ ...f, drop_points: v })} />
            <Field label="Packages"><Input data-testid="packages-input" type="number" value={f.packages} onChange={set("packages")} /></Field>
            <Field label="Weight (Tons)"><Input data-testid="weight-input" type="number" step="0.01" value={f.weight_tons} onChange={set("weight_tons")} /></Field>
            <Field label="Goods Invoice Value (₹)"><Input data-testid="invoice-value-input" type="number" value={f.invoice_value} onChange={set("invoice_value")} /></Field>
            <Field label="E-Way Bill No"><Input data-testid="eway-input" value={f.eway_bill} onChange={set("eway_bill")} /></Field>
            <Field label="Expected Delivery (ETA)"><Input data-testid="eta-input" type="date" value={f.eta || ""} onChange={set("eta")} /></Field>
            <Field label="Loading Date"><Input data-testid="loading-date-input" type="date" value={f.loading_date || ""} onChange={set("loading_date")} /></Field>
            <Field label="Unloading Date"><Input data-testid="unloading-date-input" type="date" min={f.loading_date || undefined} value={f.unloading_date || ""} onChange={set("unloading_date")} /></Field>
            <Field label="Remarks" className="sm:col-span-2"><Textarea data-testid="remarks-input" rows={2} value={f.remarks} onChange={set("remarks")} /></Field>
          </Section>
        </div>
        <div className="space-y-6">
          {!hideFreight && (
          <div className="card-base p-5 md:p-6 fade-up">
            <h3 className="mb-4 text-lg font-semibold">Freight & GST</h3>
            <div className="space-y-4">
              <Field label="Freight Calculation Type">
                <select data-testid="freight-type-select" className="native-select" value={f.freight_type} onChange={set("freight_type")}>
                  <option value="fixed">Fixed Freight (flat amount)</option>
                  <option value="per_mt">Per MT Rate (weight-based)</option>
                </select>
              </Field>
              {perMt ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Total Weight (MT)"><Input data-testid="per-mt-weight-input" type="number" step="0.01" min="0.01" required value={f.weight_tons} onChange={set("weight_tons")} /></Field>
                  <Field label="Rate per MT (₹)"><Input data-testid="rate-per-mt-input" type="number" min="1" required value={f.rate_per_mt} onChange={set("rate_per_mt")} /></Field>
                  <div className="col-span-2 rounded-md bg-accent px-3 py-2 text-xs text-accent-foreground" data-testid="per-mt-calc">
                    {Number(f.weight_tons) || 0} MT × {inr(Number(f.rate_per_mt) || 0)} = <b className="amount">{inr(freight, 2)}</b>
                  </div>
                </div>
              ) : (
                <Field label="Freight Amount (₹)"><Input data-testid="freight-input" type="number" required min="1" value={f.freight_amount} onChange={set("freight_amount")} /></Field>
              )}
              <ExtraCharges list={f.extra_charges || []} onChange={(v) => setF({ ...f, extra_charges: v })} />
              <label className="flex items-start gap-3 rounded-lg border p-3 text-sm" data-testid="rcm-toggle-row">
                <Switch data-testid="rcm-switch" checked={f.rcm} onCheckedChange={(v) => setF({ ...f, rcm: v })} />
                <span><b>Applicable Under RCM</b><span className="block text-xs text-muted-foreground">Reverse Charge Mechanism — GST not added; recipient pays GST</span></span>
              </label>
              <Field label="Advance Paid (₹)"><Input data-testid="advance-input" type="number" min="0" value={f.advance_paid} onChange={set("advance_paid")} /></Field>
              <Field label="Brokerage cut by party (₹)"><Input data-testid="lr-brokerage-input" type="number" min="0" value={f.brokerage} onChange={set("brokerage")} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GST Rate">
                  <select data-testid="gst-rate-select" disabled={f.rcm} className="native-select disabled:opacity-50" value={f.gst_rate} onChange={set("gst_rate")}>
                    {[0, 5, 18, ...(Number(f.gst_rate) === 12 ? [12] : [])].map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </Field>
                <Field label="GST Type">
                  <select data-testid="gst-type-select" disabled={f.rcm} className="native-select disabled:opacity-50" value={f.gst_type} onChange={set("gst_type")}>
                    <option value="cgst_sgst">CGST + SGST</option><option value="igst">IGST</option>
                  </select>
                </Field>
                <Field label="Bill To">
                  <select data-testid="billing-party-select" className="native-select" value={f.billing_party} onChange={set("billing_party")}>
                    <option value="consignor">Consignor</option><option value="consignee">Consignee</option>
                  </select>
                </Field>
                <Field label="GST Paid By">
                  <select data-testid="gst-paid-by-select" className="native-select" value={f.gst_paid_by} onChange={set("gst_paid_by")}>
                    <option value="consignor">Consignor</option><option value="consignee">Consignee</option><option value="transporter">Transporter</option>
                  </select>
                </Field>
              </div>
              <label className="flex items-start gap-3 rounded-lg border p-3 text-sm" data-testid="show-freight-row">
                <Switch data-testid="show-freight-switch" checked={f.show_freight_on_lr} onCheckedChange={(v) => setF({ ...f, show_freight_on_lr: v })} />
                <span><b>Show Freight Amount on LR</b><span className="block text-xs text-muted-foreground">Off prints "FIXED / AS PER AGREEMENT" on the LR copy; the invoice always shows amounts</span></span>
              </label>
              <div className="space-y-1.5 rounded-lg border bg-secondary/50 p-4 text-sm" data-testid="freight-summary">
                <div className="flex justify-between"><span>Freight</span><span className="amount">{inr(freight, 2)}</span></div>
                {extras > 0 && <div className="flex justify-between" data-testid="extras-summary"><span>Extra Charges</span><span className="amount">{inr(extras, 2)}</span></div>}
                {extras > 0 && <div className="flex justify-between font-medium"><span>Taxable Value</span><span className="amount">{inr(taxable, 2)}</span></div>}
                {f.rcm ? (
                  <div className="flex justify-between text-amber-700 dark:text-amber-400"><span>GST (RCM — payable by recipient)</span><span className="amount">{inr(0, 2)}</span></div>
                ) : f.gst_type === "cgst_sgst" ? (
                  <>
                    <div className="flex justify-between text-muted-foreground"><span>CGST {f.gst_rate / 2}%</span><span className="amount">{inr(gst / 2, 2)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>SGST {f.gst_rate / 2}%</span><span className="amount">{inr(gst / 2, 2)}</span></div>
                  </>
                ) : <div className="flex justify-between text-muted-foreground"><span>IGST {f.gst_rate}%</span><span className="amount">{inr(gst, 2)}</span></div>}
                <div className="flex justify-between border-t pt-1.5 font-semibold"><span>Total</span><span className="amount" data-testid="freight-total">{inr(total, 2)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Balance after advance{Number(f.brokerage) > 0 ? " & brokerage" : ""}</span><span className="amount">{inr(total - Number(f.advance_paid || 0) - Number(f.brokerage || 0), 2)}</span></div>
              </div>
            </div>
          </div>
          )}
          <div className="card-base p-5 md:p-6 fade-up">
            <h3 className="mb-4 text-lg font-semibold">Assign Vehicle & Driver</h3>
            {existingMt ? (
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm dark:border-violet-800 dark:bg-violet-950/30" data-testid="existing-market-hire">
                Market Hire: <b className="font-mono">{existingMt.vehicle_number}</b> — <a href={`/market/trips/${existingMt.id}`} className="font-semibold text-primary hover:underline">open market trip</a> to edit vehicle / vendor.
              </div>
            ) : (
            <>
            <div className="mb-4 grid grid-cols-2 gap-2" data-testid="hire-mode-toggle">
              {[["own", "Apni Gaadi"], ["market", "Market Hire"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`hire-mode-${v}`} onClick={() => setHireMode(v)}
                  className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${hireMode === v ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:border-ring"}`}>{l}</button>
              ))}
            </div>
            {hireMode === "market" ? <MarketHireFields m={mh} setM={setMh} lrWeight={Number(f.weight_tons) || 0} showFreight={["admin", "manager"].includes(role)} /> : (
            <div className="space-y-4">
              <Field label="Vehicle">
                <select data-testid="assign-vehicle-select" className="native-select" value={f.vehicle_id || ""} onChange={onVehicle}>
                  <option value="">— Assign later —</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id} disabled={v.status !== "Available" && v.id !== f.vehicle_id}>{v.vehicle_number} · {v.vehicle_type} · {v.status}</option>)}
                </select>
              </Field>
              <Field label="Driver">
                {useAutoDriver ? (
                  <div data-testid="auto-driver-box" className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-800 dark:bg-emerald-950/30">
                    <div className="min-w-0">
                      <span className="font-semibold">{currentDriver.name}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">{currentDriver.phone}</span>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Auto-assigned — current driver of this vehicle</div>
                    </div>
                    <button type="button" data-testid="change-driver-toggle" onClick={() => setDriverOverride(true)}
                      className="flex shrink-0 items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs font-semibold hover:border-primary">
                      <UserCog className="h-3.5 w-3.5" /> Change driver
                    </button>
                  </div>
                ) : (
                  <>
                    <select data-testid="assign-driver-select" className="native-select" value={f.driver_id || ""} onChange={set("driver_id")}>
                      <option value="">— Assign later —</option>
                      {drivers.filter((d) => d.employment_status === "Active").map((d) => <option key={d.id} value={d.id}>{d.name}{d.on_trip ? " (on trip)" : ""}{currentDriver && d.id === currentDriver.id ? " (current)" : ""}</option>)}
                    </select>
                    {currentDriver && (
                      <button type="button" data-testid="revert-driver-btn" onClick={() => { setDriverOverride(false); setF({ ...f, driver_id: currentDriver.id }); }}
                        className="mt-1 text-xs font-semibold text-primary hover:underline">← Use regular driver ({currentDriver.name})</button>
                    )}
                  </>
                )}
              </Field>
              {f.vehicle_id && !currentDriver && !f.driver_id && (
                <p data-testid="no-driver-hint" className="text-xs font-medium text-amber-600">No current driver is assigned to this vehicle in the Fleet module — pick one manually.</p>
              )}
            </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
